import { z } from 'zod';

import { type ApiResult } from '@/lib/api-helpers/endpoint-manager';
import { regionToMainCountry } from '@/lib/utils';
import { type Region } from '@/types';

export type PhoneInputs = {
  phoneNumber: string;
  country: string;
};

type PhoneValidationMessages = {
  phoneInvalidMessage: string;
  phoneRequiredMessage: string;
};

const PHONE_MIN_LENGTH = 4;

export const getPhoneDefaults = (region: Region): PhoneInputs => ({
  country: regionToMainCountry(region),
  phoneNumber: '',
});

export const getBaseSchema = (messages?: PhoneValidationMessages) =>
  z.object(
    {
      phoneNumber: z
        .string({ required_error: messages?.phoneRequiredMessage })
        .min(PHONE_MIN_LENGTH, messages?.phoneInvalidMessage),
      country: z.string({
        required_error: messages?.phoneRequiredMessage,
      }),
    },
    {
      invalid_type_error: messages?.phoneInvalidMessage,
      required_error: messages?.phoneRequiredMessage,
    }
  );

export const getPhoneSchema = (
  messages: PhoneValidationMessages,
  options?: Partial<{ isInputActive: boolean }>
): z.ZodEffects<z.ZodOptional<z.ZodType<PhoneInputs>>> => {
  const { isInputActive } = { isInputActive: true, ...options };

  return getBaseSchema(messages)
    .optional()
    .refine(async (phone): Promise<boolean> => {
      // Return early to avoid extra API requests
      if (!isInputActive) return true;
      if (!phone || phone.phoneNumber.length < PHONE_MIN_LENGTH) return false;

      let response;
      try {
        response = await fetch('/api/phone-validations', {
          body: JSON.stringify(phone),
          headers: { 'Content-Type': 'application/json' },
          method: 'POST',
        });
      } catch {
        return false;
      }
      if (!response.ok) return false;

      const {
        result: { valid },
      } = (await response.json()) as ApiResult<{ valid: boolean }>;

      return valid;
    }, messages.phoneInvalidMessage);
};
